import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AvailableThinker } from "~/api/aiAdminTypes";
import type { CanopyScope } from "~/api/canopyTypes";
import { useApi } from "~/api/provider";
import { UUID_PATTERN } from "~/api/reasoningRequestIds";
import {
  REASONING_EFFORTS,
  type ReasoningEffort,
  type ReasoningRecord,
  type ReasoningSession,
} from "~/api/reasoningTypes";
import type { Run } from "~/api/types";
import { ActionRequired, failureKind } from "~/foundation/components/ActionRequired";
import { Button } from "~/foundation/components/Button";
import { Spinner } from "~/foundation/components/Spinner";
import { REASONING_SCENARIOS, type ReasoningLaunch } from "./reasoningLaunch";
import { isTerminalJobStatus, phaseOf, plainRunStatus, readSession } from "./reasoningSelectors";
import { pruneReasoningRecovery } from "./recoveryStorage";
import { useReasoningRequest } from "./useReasoningRequest";
import { budgetedWords, useReasoningSupport } from "./useReasoningSupport";

/**
 * `/reasoning` — ask a question, watch it being worked out, read the answer, say whether it is
 * right. Owner 2026-10-17: *"regular people should be able to use this and understand from what
 * they see"*. So the page speaks in the reader's words only. Everything the engine needs beyond
 * the question (intent vectors, objectives, criteria, token windows) is DERIVED here from the
 * plain choices and never shown unless the reader opens "Fine-tune".
 */

type ScenarioId = (typeof REASONING_SCENARIOS)[number]["id"] | "question";
type EffortMode = "budgeted" | "native";

/** The one acceptance criterion every request carries: the answer must stand on evidence. */
const GROUNDED =
  "Address the question with pinned evidence; distinguish observations, proposals and uncertainty.";
/** The server accepts a window of 4,096–1,000,000 tokens and a reserve of at least 1. */
const DEFAULT_WINDOW = 8192;
const MIN_WINDOW = 4096;
const MAX_WINDOW = 1_000_000;
/** The reserve defaults to the chosen model's own output limit, never above this. */
const MAX_DEFAULT_RESERVE = 4096;
export const SESSION_POLL_MS = 3000;

function defaultPerspectives() {
  return [
    { role: "Developer", intent: "Implementation correctness and maintainability", weight: 5 },
    { role: "Reviewer", intent: "Evidence, regressions and counterexamples", weight: 5 },
    { role: "Architect", intent: "System boundaries and long-term fit", weight: 3 },
    { role: "Operator", intent: "Recovery, observability and rollout", weight: 3 },
  ];
}

const lines = (text: string) =>
  text
    .split("\n")
    .map((x) => x.trim())
    .filter(Boolean)
    .slice(0, 8);

const text = (value: unknown) => (typeof value === "string" ? value : "");
const count = (value: unknown) => (Array.isArray(value) ? value.length : 0);

export function ReasoningHubScene({ launch = {} }: { launch?: ReasoningLaunch }) {
  const { t } = useTranslation();
  const api = useApi();
  const { budgetedLimits } = useReasoningSupport();
  // Journals older than two weeks belong to questions nobody will replay.
  useEffect(() => {
    pruneReasoningRecovery();
  }, []);

  // ── what the reader chooses ───────────────────────────────────────────────
  const [question, setQuestion] = useState("");
  const [scenario, setScenario] = useState<ScenarioId>("question");
  const [effort, setEffort] = useState<ReasoningEffort>("medium");
  const [thinkerId, setThinkerId] = useState("");
  const [scopeSlug, setScopeSlug] = useState(launch.scope ?? "");

  // ── fine-tuning, folded away ──────────────────────────────────────────────
  const [effortMode, setEffortMode] = useState<EffortMode>("budgeted");
  const [evaluatorId, setEvaluatorId] = useState("");
  const [perspectives, setPerspectives] = useState(defaultPerspectives);
  const [requiredIds, setRequiredIds] = useState(launch.source ?? "");
  const [workItemId, setWorkItemId] = useState("");
  const [modelWindow, setModelWindow] = useState(DEFAULT_WINDOW);
  const [outputReserve, setOutputReserve] = useState(MAX_DEFAULT_RESERVE);
  const [reserveTouched, setReserveTouched] = useState(false);
  const [avoidText, setAvoidText] = useState("");
  const [expectText, setExpectText] = useState("");

  // ── what the server has ───────────────────────────────────────────────────
  const [thinkers, setThinkers] = useState<AvailableThinker[] | null>(null);
  const [scopes, setScopes] = useState<CanopyScope[] | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [loadError, setLoadError] = useState("");
  useEffect(() => {
    let live = true;
    Promise.allSettled([
      api.thinkers.available(),
      api.automation.runs.list({ limit: 40 }),
      api.canopy.scopes(),
    ]).then(([agents, recent, canopy]) => {
      if (!live) return;
      setThinkers(
        agents.status === "fulfilled"
          ? agents.value.filter((x) => x.isAi && x.isLocal && x.usable)
          : [],
      );
      setScopes(canopy.status === "fulfilled" ? canopy.value.filter((s) => s.enabled) : []);
      if (recent.status === "fulfilled")
        setRuns(recent.value.filter((run) => run.kind.toLowerCase() === "reasoning"));
      if (agents.status === "rejected" || canopy.status === "rejected")
        setLoadError(t("Some of this page could not be loaded. Refresh to try again."));
    });
    return () => {
      live = false;
    };
  }, [api, t]);

  const selected = thinkers?.find((x) => x.id === thinkerId) ?? thinkers?.[0];
  const evaluator = thinkers?.find((x) => x.id === evaluatorId);
  const scope = scopes?.find((s) => s.slug === scopeSlug) ?? scopes?.[0];

  const [capabilities, setCapabilities] = useState<Awaited<
    ReturnType<typeof api.reasoning.localCapabilities>
  > | null>(null);
  const [capabilityError, setCapabilityError] = useState("");
  useEffect(() => {
    let active = true;
    setCapabilities(null);
    setCapabilityError("");
    if (selected)
      api.reasoning
        .localCapabilities(selected.id)
        .then((result) => {
          if (!active) return;
          setCapabilities(result);
          // The answer reserve follows the model's own output limit until the reader sets it.
          if (!reserveTouched && Number.isSafeInteger(result.maxOutputTokens))
            setOutputReserve(Math.max(1, Math.min(result.maxOutputTokens, MAX_DEFAULT_RESERVE)));
        })
        .catch((error) => {
          if (active) setCapabilityError(error instanceof Error ? error.message : String(error));
        });
    return () => {
      active = false;
    };
  }, [api, selected, reserveTouched]);

  const supportsThinking = capabilities?.supportsThinking === true;
  const modelReady =
    !!selected?.model &&
    capabilities?.model === selected.model &&
    (effortMode === "budgeted" ||
      (supportsThinking &&
        (!capabilities.nativeLevelsVerified || capabilities.effortLevels.includes(effort))));

  // ── the current answer, polled while a request is retained ────────────────
  const [session, setSession] = useState<ReasoningSession | null>(null);
  const [verdictError, setVerdictError] = useState("");
  const [accepting, setAccepting] = useState(false);
  const { candidate, evaluation, accepted, qualified } = readSession(session);
  const request = useReasoningRequest({ evaluated: !!evaluation });
  const busy = request.busy;
  const runId = request.pending?.runId ?? null;
  // Nothing more will change once the reader accepted, or once the job ended without a result
  // to wait for (failed, stopped, uncertain) or with its evaluation already recorded.
  const sessionSettled =
    accepted ||
    (isTerminalJobStatus(request.jobStatus) &&
      (request.jobStatus.toLowerCase() !== "completed" || !!evaluation));
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new run resets the answer; a settled one stops the poll.
  useEffect(() => {
    setSession(null);
    setVerdictError("");
  }, [runId]);
  useEffect(() => {
    if (!runId || sessionSettled) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const found = (await api.reasoning.byRun(runId)).session;
        if (live) setSession(found);
      } catch {
        /* The run exists before its session does; the request status reports real errors. */
      }
      if (live) timer = setTimeout(poll, SESSION_POLL_MS);
    };
    void poll();
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [api, runId, sessionSettled]);

  // ── why "Ask" is not available yet, in the reader's words ─────────────────
  const invalidWorkItem = !!workItemId.trim() && !UUID_PATTERN.test(workItemId.trim());
  const invalidWindow =
    !Number.isSafeInteger(modelWindow) ||
    modelWindow < MIN_WINDOW ||
    modelWindow > MAX_WINDOW ||
    !Number.isSafeInteger(outputReserve) ||
    outputReserve < 1;
  const invalidPerspectives = perspectives.some(
    (p) => !p.intent.trim() || !Number.isSafeInteger(p.weight) || p.weight < 1 || p.weight > 5,
  );
  const blocker = !request.ready
    ? t("Signing you in…")
    : thinkers === null || scopes === null
      ? t("Loading…")
      : thinkers.length === 0
        ? t("No model is available on this computer yet.")
        : scopes.length === 0
          ? t("There is nothing to read yet. Build a knowledge tree first.")
          : !question.trim()
            ? t("Type a question to start.")
            : capabilityError
              ? capabilityError
              : effortMode === "native" && capabilities?.supportsThinking === false
                ? t(
                    "This model cannot be asked to think longer. Choose another, or let it answer at its usual pace.",
                  )
                : effortMode === "native" && capabilities?.supportsThinking === null
                  ? t("This model has not said whether it can think longer.")
                  : capabilities === null || !modelReady
                    ? t("Checking the model…")
                    : invalidWorkItem
                      ? t("The work item reference does not look right.")
                      : invalidWindow
                        ? t("The memory sizes under Fine-tune do not fit together.")
                        : invalidPerspectives
                          ? t(
                              "Every viewpoint under Fine-tune needs a sentence and a weight from 1 to 5.",
                            )
                          : "";
  const canAsk = !busy && !request.pending && !blocker;

  const ask = async () => {
    if (!canAsk || !selected?.model || !scope) return;
    const chosen = REASONING_SCENARIOS.find((row) => row.id === scenario);
    const focus = launch.source
      ? ` ${t("Focus on the selected lines {{start}}–{{end}} of source canopy:{{source}}.", {
          start: launch.startLine,
          end: launch.endLine,
          source: launch.source,
        })}`
      : "";
    const query = chosen
      ? `${question.trim()}\n\n${chosen.query}${focus}`
      : `${question.trim()}${focus}`;
    const toward = chosen?.toward ?? "Answer the question with evidence from the chosen knowledge";
    const signal =
      chosen?.signal ?? "The person who asked reads the answer and marks it right or wrong";
    setVerdictError("");
    await request.submit({
      thinkerId: selected.id,
      model: selected.model,
      effort,
      effortMode,
      evaluatorThinkerId: evaluator?.id,
      evaluatorModel: evaluator?.model || undefined,
      workItemId: workItemId.trim() || undefined,
      scopeSlug: scope.slug,
      query,
      modelContextWindow: modelWindow,
      outputReserve,
      requiredNodeIds: [
        ...new Set([
          ...(launch.source ? [launch.source] : []),
          ...requiredIds.split(/[\s,]+/).filter(Boolean),
        ]),
      ],
      requiredSourceDigests:
        launch.source && launch.digest ? { [launch.source]: launch.digest } : undefined,
      purpose: {
        perspectives,
        intentKey: `reasoning:${chosen?.id ?? "question"}`,
        intentRevision: "1",
        intent: {
          in: chosen?.label ?? question.trim().slice(0, 500),
          toward,
          amplitude: 0.5,
          pursuit: "expand",
          avoid: lines(avoidText),
          expect: lines(expectText),
        },
        objectives: [{ id: "primary", outcome: toward, successSignal: signal, weight: 5 }],
      },
      criteria: [{ id: "grounded", description: GROUNDED, hard: true, requiresEvidence: true }],
    });
  };

  const accept = async () => {
    if (!session || !candidate || !evaluation || accepting) return;
    setAccepting(true);
    setVerdictError("");
    try {
      await api.reasoning.accept({
        sessionId: session.id,
        expectedSessionRevision: session.revision,
        candidateRecordId: candidate.id,
        evaluationRecordId: evaluation.id,
        commandId: crypto.randomUUID(),
      });
      setSession((await api.reasoning.byRun(session.runId)).session);
    } catch (e) {
      setVerdictError(e instanceof Error ? e.message : String(e));
    } finally {
      setAccepting(false);
    }
  };

  const askAgain = () => {
    if (request.pending) setQuestion(request.pending.input.query.split("\n")[0] ?? "");
    request.clear();
  };

  const scenarioCards: { id: ScenarioId; title: string; hint: string }[] = [
    {
      id: "question",
      title: t("Just answer"),
      hint: t("A plain answer, with the sources it rests on."),
    },
    {
      id: "source-correction",
      title: t("Check what is still true"),
      hint: t(
        "Compares earlier conclusions with what the sources say now and points out what went stale.",
      ),
    },
    {
      id: "conflicting-contributions",
      title: t("Weigh conflicting advice"),
      hint: t("Lines up the different opinions with their evidence and proposes a decision."),
    },
    {
      id: "causal-attention",
      title: t("Find causes and what to check first"),
      hint: t(
        "Looks at real prerequisites before coincidences and suggests the next thing to verify.",
      ),
    },
    {
      id: "retry-and-recovery",
      title: t("Plan a recovery"),
      hint: t(
        "After something broke: safe steps back, when to stop, and how to confirm it worked.",
      ),
    },
  ];
  const effortLabels: Record<ReasoningEffort, string> = {
    low: t("Quick"),
    medium: t("Normal"),
    high: t("Thorough"),
    max: t("Maximum"),
  };

  return (
    <main className="mx-auto flex w-full max-w-[760px] flex-col gap-8 p-6">
      <header className="flex flex-col gap-2">
        <h1 className="font-semibold text-2xl text-text">{t("Reasoning")}</h1>
        <p className="text-base text-text-secondary">
          {t(
            "Ask a question about your workspace. A model running on your own computer reads the knowledge you choose, works out an answer, checks it, and shows you the result with its sources.",
          )}
        </p>
      </header>

      {loadError ? (
        <p role="alert" className="text-danger">
          {loadError}
        </p>
      ) : null}

      {request.pending ? (
        <Progress
          question={request.pending.input.query.split("\n")[0] ?? ""}
          runId={request.pending.runId}
          jobStatus={request.jobStatus}
          detail={request.status}
          error={request.error}
          busy={busy}
          session={session}
          candidate={candidate}
          evaluation={evaluation}
          qualified={qualified}
          accepted={accepted}
          accepting={accepting}
          verdictError={verdictError}
          onStop={() => void request.cancel()}
          onRetry={() => void request.submit()}
          onReconcile={() => void request.reconcile()}
          onAccept={() => void accept()}
          onAskAgain={askAgain}
        />
      ) : (
        <form
          className="flex flex-col gap-6"
          onSubmit={(e) => {
            e.preventDefault();
            void ask();
          }}
        >
          <label className="flex flex-col gap-2">
            <span className="font-medium text-text">{t("What do you want to know?")}</span>
            <textarea
              aria-label={t("Your question")}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={4}
              maxLength={3000}
              placeholder={t("e.g. Why did the release slip, and what should we check first?")}
              className="rounded border border-input-border bg-bg p-3 text-base text-text"
            />
          </label>
          {launch.source ? (
            <p className="text-sm text-text-secondary">
              {t("It will also read the lines you selected in VS Code ({{start}}–{{end}}).", {
                start: launch.startLine,
                end: launch.endLine,
              })}
            </p>
          ) : null}

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 font-medium text-text">{t("What kind of help?")}</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {scenarioCards.map((card) => (
                <label
                  key={card.id}
                  className={`flex cursor-pointer gap-3 rounded border p-3 ${
                    scenario === card.id ? "border-accent" : "border-divider hover:bg-hover-bg"
                  }`}
                >
                  <input
                    type="radio"
                    name="scenario"
                    value={card.id}
                    checked={scenario === card.id}
                    onChange={() => setScenario(card.id)}
                    className="mt-1"
                  />
                  <span className="flex flex-col gap-1">
                    <span className="font-medium text-text">{card.title}</span>
                    <span className="text-sm text-text-secondary">{card.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <Choice
              label={t("What to read")}
              value={scope?.slug ?? ""}
              onChange={setScopeSlug}
              disabled={!!launch.source}
              options={(scopes ?? []).map((s) => ({ value: s.slug, label: s.name }))}
              empty={
                <Link to="/knowledge" className="text-accent underline">
                  {t("Build a knowledge tree first")}
                </Link>
              }
            />
            <Choice
              label={t("Who answers")}
              value={selected?.id ?? ""}
              onChange={setThinkerId}
              options={(thinkers ?? []).map((x) => ({
                value: x.id,
                label: x.model ? `${x.name} · ${x.model}` : x.name,
              }))}
              empty={
                <Link
                  to="/settings/$section"
                  params={{ section: "agents" }}
                  className="text-accent underline"
                >
                  {t("Set up a model on this computer")}
                </Link>
              }
            />
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 font-medium text-text">{t("How hard should it think?")}</legend>
            <div role="radiogroup" aria-label={t("Effort")} className="flex flex-wrap gap-2">
              {REASONING_EFFORTS.map((level) => (
                <button
                  key={level}
                  type="button"
                  role="radio"
                  aria-checked={effort === level}
                  onClick={() => setEffort(level)}
                  className={`rounded-full border px-3 py-1 text-sm ${
                    effort === level
                      ? "border-accent text-text"
                      : "border-divider text-text-secondary hover:bg-hover-bg"
                  }`}
                >
                  {effortLabels[level]}
                </button>
              ))}
            </div>
            <p className="text-sm text-text-secondary">
              {t("Harder means a longer, more careful answer, and a longer wait.")}
            </p>
          </fieldset>

          <details className="rounded border border-divider p-3">
            <summary className="cursor-pointer text-sm text-text-secondary">
              {t("Fine-tune")}
            </summary>
            <div className="mt-3 flex flex-col gap-4">
              <label className="flex flex-col gap-1 text-sm">
                <span>{t("How the effort is applied")}</span>
                <select
                  aria-label={t("Effort mode")}
                  value={effortMode}
                  onChange={(e) => setEffortMode(e.target.value as EffortMode)}
                  className="rounded border border-input-border bg-bg p-2"
                >
                  <option value="budgeted">{t("Limit the length of the answer")}</option>
                  <option value="native">{t("Let the model itself think longer")}</option>
                </select>
                <span className="text-text-secondary">
                  {effortMode === "budgeted"
                    ? budgetedLimits
                      ? t(
                          "Quick, Normal, Thorough and Maximum cap the answer at about {{low}}, {{medium}}, {{high}} and {{max}} words.",
                          {
                            low: budgetedWords(budgetedLimits.low).toLocaleString(),
                            medium: budgetedWords(budgetedLimits.medium).toLocaleString(),
                            high: budgetedWords(budgetedLimits.high).toLocaleString(),
                            max: budgetedWords(budgetedLimits.max).toLocaleString(),
                          },
                        )
                      : t("Quick, Normal, Thorough and Maximum cap the length of the answer.")
                    : supportsThinking && !capabilities?.nativeLevelsVerified
                      ? t(
                          "This model can think longer, but has not said how much each level changes.",
                        )
                      : t("Only models that can think longer accept this.")}
                </span>
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span>{t("A second model to check the answer")}</span>
                <select
                  aria-label={t("Checking model")}
                  value={evaluatorId}
                  onChange={(e) => setEvaluatorId(e.target.value)}
                  className="rounded border border-input-border bg-bg p-2"
                >
                  <option value="">{t("The same model checks its own answer")}</option>
                  {(thinkers ?? []).map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.model ? `${x.name} · ${x.model}` : x.name}
                    </option>
                  ))}
                </select>
              </label>
              <fieldset className="flex flex-col gap-2 text-sm">
                <legend>{t("Viewpoints to weigh")}</legend>
                <p className="text-text-secondary">
                  {t(
                    "Each viewpoint reads the question in its own way; the weight says how much it counts.",
                  )}
                </p>
                {perspectives.map((perspective, index) => (
                  <div className="flex items-center gap-2" key={perspective.role}>
                    <strong className="w-24">{perspective.role}</strong>
                    <input
                      aria-label={`${perspective.role} intent`}
                      value={perspective.intent}
                      onChange={(e) =>
                        setPerspectives((rows) =>
                          rows.map((row, i) =>
                            i === index ? { ...row, intent: e.target.value } : row,
                          ),
                        )
                      }
                      className="flex-1 rounded border border-input-border bg-bg p-1"
                    />
                    <input
                      aria-label={`${perspective.role} weight`}
                      type="number"
                      min={1}
                      max={5}
                      value={perspective.weight}
                      onChange={(e) =>
                        setPerspectives((rows) =>
                          rows.map((row, i) =>
                            i === index ? { ...row, weight: Number(e.target.value) } : row,
                          ),
                        )
                      }
                      className="w-16 rounded border border-input-border bg-bg p-1"
                    />
                  </div>
                ))}
              </fieldset>
              <label className="flex flex-col gap-1 text-sm">
                <span>{t("Leave out of focus (one per line)")}</span>
                <textarea
                  value={avoidText}
                  onChange={(e) => setAvoidText(e.target.value)}
                  rows={2}
                  className="rounded border border-input-border bg-bg p-2"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span>{t("Assumptions to challenge (one per line)")}</span>
                <textarea
                  value={expectText}
                  onChange={(e) => setExpectText(e.target.value)}
                  rows={2}
                  className="rounded border border-input-border bg-bg p-2"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span>{t("Sources it must read (one reference per line)")}</span>
                <textarea
                  aria-label={t("Required sources")}
                  value={requiredIds}
                  onChange={(e) => setRequiredIds(e.target.value)}
                  rows={2}
                  className="rounded border border-input-border bg-bg p-2"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span>{t("Work item to keep in mind (optional)")}</span>
                <input
                  aria-label={t("Work item")}
                  value={workItemId}
                  onChange={(e) => setWorkItemId(e.target.value)}
                  placeholder={t("Paste the work item's reference")}
                  className="rounded border border-input-border bg-bg p-2"
                />
              </label>
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="flex flex-col gap-1">
                  <span>{t("Model memory (tokens)")}</span>
                  <input
                    aria-label={t("Model memory")}
                    type="number"
                    min={MIN_WINDOW}
                    max={MAX_WINDOW}
                    value={modelWindow}
                    onChange={(e) => setModelWindow(Number(e.target.value))}
                    className="w-32 rounded border border-input-border bg-bg p-2"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span>{t("Kept for the answer (tokens)")}</span>
                  <input
                    aria-label={t("Answer reserve")}
                    type="number"
                    min={1}
                    value={outputReserve}
                    onChange={(e) => {
                      setReserveTouched(true);
                      setOutputReserve(Number(e.target.value));
                    }}
                    className="w-32 rounded border border-input-border bg-bg p-2"
                  />
                </label>
              </div>
            </div>
          </details>

          <div className="flex flex-col gap-2">
            <div>
              <Button type="submit" variant="primary" disabled={!canAsk}>
                {busy ? t("Asking…") : t("Ask")}
              </Button>
            </div>
            {blocker ? (
              <p role="status" className="text-sm text-text-secondary">
                {blocker}
              </p>
            ) : null}
            {request.error ? (
              <p role="alert" className="text-sm text-danger">
                {request.error}
              </p>
            ) : null}
          </div>
        </form>
      )}

      <section className="flex flex-col gap-2">
        <h2 className="font-semibold text-lg text-text">{t("Earlier questions")}</h2>
        {runs.length ? (
          <ul className="flex flex-col gap-2">
            {runs.map((run) => (
              <li key={run.id}>
                <Link
                  to="/runs/$runId"
                  params={{ runId: run.id }}
                  className="flex items-baseline justify-between gap-3 rounded border border-divider p-3 hover:bg-hover-bg"
                >
                  <span className="truncate text-text">{run.name.split("\n")[0]}</span>
                  <span className="shrink-0 text-sm text-text-secondary">
                    {plainRunStatus(run.status, t)} · {dayOf(run.createdAt)}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-text-secondary">{t("Nothing asked yet.")}</p>
        )}
      </section>
    </main>
  );
}

function Choice({
  label,
  value,
  onChange,
  options,
  disabled,
  empty,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  empty: React.ReactNode;
}) {
  const current = options.find((o) => o.value === value) ?? options[0];
  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium text-text">{label}</span>
      {options.length === 0 ? (
        <span className="text-sm">{empty}</span>
      ) : options.length === 1 || disabled ? (
        <span className="text-sm text-text-secondary">{current?.label}</span>
      ) : (
        <select
          aria-label={label}
          value={current?.value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="rounded border border-input-border bg-bg p-2 text-sm"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/** What is happening to the question right now, and the answer once there is one. */
function Progress({
  question,
  runId,
  jobStatus,
  detail,
  error,
  busy,
  session,
  candidate,
  evaluation,
  qualified,
  accepted,
  accepting,
  verdictError,
  onStop,
  onRetry,
  onReconcile,
  onAccept,
  onAskAgain,
}: {
  question: string;
  runId: string;
  jobStatus: string;
  detail: string;
  error: string;
  busy: boolean;
  session: ReasoningSession | null;
  candidate: ReasoningRecord | null;
  evaluation: ReasoningRecord | null;
  qualified: boolean;
  accepted: boolean;
  accepting: boolean;
  verdictError: string;
  onStop: () => void;
  onRetry: () => void;
  onReconcile: () => void;
  onAccept: () => void;
  onAskAgain: () => void;
}) {
  const { t } = useTranslation();
  const phase = phaseOf(jobStatus, !!evaluation, !!error);
  const stages = [
    { label: t("Reading the knowledge"), done: !!session },
    { label: t("Writing an answer"), done: !!candidate },
    { label: t("Checking the answer"), done: !!evaluation },
    { label: t("Your verdict"), done: accepted },
  ];
  const working = phase === "waiting" || phase === "thinking";
  const sentence: Record<ReturnType<typeof phaseOf>, string> = {
    waiting: t("Waiting for the model to be free…"),
    thinking: t("Thinking…"),
    done: t("Done."),
    failed: t("Something went wrong."),
    stopped: t("Stopped."),
    uncertain: t("The computer lost track of whether the model finished."),
  };
  return (
    <section
      aria-label={t("Your question")}
      className="flex flex-col gap-4 rounded border border-divider p-4"
    >
      <div className="flex flex-col gap-1">
        <p className="text-sm text-text-secondary">{t("Your question")}</p>
        <p className="font-medium text-base text-text">{question}</p>
      </div>

      <ol aria-label={t("Progress")} className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {stages.map((stage, index) => {
          const current = !stage.done && stages.slice(0, index).every((s) => s.done) && working;
          return (
            <li
              key={stage.label}
              aria-current={current ? "step" : undefined}
              className={`flex items-center gap-2 rounded border p-2 text-sm ${
                stage.done ? "border-accent text-text" : "border-divider text-text-secondary"
              }`}
            >
              {current ? <Spinner size={12} /> : <span aria-hidden>{stage.done ? "✓" : "·"}</span>}
              {stage.label}
            </li>
          );
        })}
      </ol>

      <p role="status" className="text-text">
        {sentence[phase]}
      </p>
      {phase === "failed" ? (
        <ActionRequired error={error || detail} onRecheck={onRetry} busy={busy} />
      ) : phase === "uncertain" ? (
        <p className="text-sm text-text-secondary">{error || detail}</p>
      ) : null}
      {phase === "uncertain" ? (
        <p className="text-sm text-text-secondary">
          {t("If an answer appears below, the model did finish. If not, it is safe to ask again.")}
        </p>
      ) : null}

      {candidate ? (
        <article className="flex flex-col gap-3 rounded border border-divider bg-bg p-4">
          <h2 className="font-semibold text-lg text-text">{t("Answer")}</h2>
          <p className="whitespace-pre-wrap text-base text-text">
            {text(candidate.payload.content)}
          </p>
          <p className="text-sm text-text-secondary">
            {t("Sources used: {{n}}", { n: count(candidate.payload.evidence) })}
          </p>
          {evaluation ? (
            qualified ? (
              <p className="text-sm text-text">
                {t("The check passed: the answer rests on its sources.")}
              </p>
            ) : (
              <div className="flex flex-col gap-1 text-sm">
                <p className="text-danger">{t("The check found problems with this answer.")}</p>
                {text(evaluation.payload.rationale) ? (
                  <p className="text-text-secondary">{text(evaluation.payload.rationale)}</p>
                ) : null}
              </div>
            )
          ) : working ? (
            <p className="text-sm text-text-secondary">{t("Still checking it…")}</p>
          ) : null}
          {accepted ? (
            <p className="text-sm text-text">{t("You marked this answer as right.")}</p>
          ) : evaluation && qualified && candidate.eligible && evaluation.eligible ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" disabled={accepting} onClick={onAccept}>
                {t("Looks right")}
              </Button>
              <Button variant="neutral" disabled={busy} onClick={onAskAgain}>
                {t("Not quite, ask again")}
              </Button>
            </div>
          ) : null}
          {verdictError ? (
            <p role="alert" className="text-sm text-danger">
              {verdictError}
            </p>
          ) : null}
        </article>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {working ? (
          <Button variant="neutral" disabled={busy} onClick={onStop}>
            {t("Stop")}
          </Button>
        ) : null}
        {(phase === "failed" && ["other", "connection"].includes(failureKind(error || detail))) ||
        phase === "uncertain" ? (
          <Button variant="primary" disabled={busy} onClick={onRetry}>
            {t("Ask again")}
          </Button>
        ) : null}
        {phase === "uncertain" ? (
          <Button variant="neutral" disabled={busy} onClick={onReconcile}>
            {t("It did finish, carry on")}
          </Button>
        ) : null}
        {!working || accepted ? (
          <Button variant="neutral" disabled={busy} onClick={onAskAgain}>
            {t("Ask another question")}
          </Button>
        ) : (
          <Button variant="ghost" disabled={busy} onClick={onAskAgain}>
            {t("Ask another question (this one keeps going)")}
          </Button>
        )}
        <Link
          to="/runs/$runId"
          params={{ runId }}
          className="ms-auto text-sm text-text-secondary underline"
        >
          {t("See the full record")}
        </Link>
      </div>
    </section>
  );
}

function dayOf(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString();
}
